import React, { useState } from 'react';
import {
  Book, ListOrdered, Mic, Monitor, Music, Search, Smartphone, Tv, Wand2, X, ChevronRight, ChevronLeft,
  CloudOff, Layers,
} from 'lucide-react';

const icon = (Icon) => <Icon className="w-8 h-8 text-amber-400" />;

// The same walkthrough is used on the front page and from Help > Take a tour.
export const TOUR_STEPS = [
  {
    title: 'Bible Verse Search',
    icon: icon(Search),
    description: 'Type a reference like "John 3:16" or "1 cor 13", or press Browse books and click your way to a book, chapter and verses. Next and previous chapter buttons let you page through a book. Switch between KJV, NIV and more at any time.',
    tip: 'Finding a verse only stages it. Nothing reaches the congregation until you send it live.',
  },
  {
    title: 'Staging & Live',
    icon: icon(Monitor),
    description: 'Everything goes to Staging first so you can check it privately. Press Live to send it to the congregation screen, then use the LIVE switch at the top. The header always says what the congregation can see.',
    tip: 'Open Display, then drag that window onto the projector. Press B to clear the screen at any time.',
  },
  {
    title: 'Service Run-sheet',
    icon: icon(ListOrdered),
    description: 'Plan the service ahead: add scriptures, songs and text slides to a run-sheet. On Sunday step through it with the arrow keys, press Enter to go live, and reorder or skip items as the service changes.',
    tip: 'Add a staged verse or song with "+ Plan". Use "Prepare offline" to save the verses before the service.',
  },
  {
    title: 'Slides for Long Passages & Songs',
    icon: icon(Layers),
    description: 'Long Bible passages are split into screen-sized slides by sentence and verse. Songs can show one to four lines at a time. Step through them with the up and down arrow keys or the buttons on the Live card.',
    tip: 'Change the sizes in Settings under "Lyrics per slide" and "Long Bible passages".',
  },
  {
    title: 'Voice Detection',
    icon: icon(Mic),
    description: 'Press Start and the app listens to the preacher. When a reference like "John 3:16" is spoken it is staged for you, and SleekHear AI can catch verses that are quoted without being named.',
    tip: 'Works in Chrome and Edge. Pick the right microphone under Voice > Options.',
  },
  {
    title: 'Songs & the AI Song Finder',
    icon: icon(Music),
    description: 'Keep your own song library, find songs online, or let the AI listen: say the title or sing a few lines and it suggests the song. Show lyrics one line or one stanza at a time.',
    tip: 'Add a CCLI number to each song, then open Usage report for a CSV of what was shown.',
  },
  {
    title: 'Projector, Stage & Livestream Screens',
    icon: icon(Tv),
    description: 'Open Display for the projector, Stage for the preacher and band (current slide, next slide, next item and a clock), and add the OBS link as a Browser source to put verses and lyrics on your livestream as a lower third.',
    tip: 'Backgrounds, fonts, text shadow and outline are all under Settings.',
  },
  {
    title: 'Phone Remote',
    icon: icon(Smartphone),
    description: 'Click Remote and scan the QR code. A volunteer can move through the run-sheet, send slides live, clear the screen and step lyrics from their phone. No password is shared and the code ends with the session.',
    tip: 'Press New code or End session in the Remote panel to disconnect a phone straight away.',
  },
  {
    title: 'Works Offline, on Every Computer',
    icon: icon(CloudOff),
    description: 'Your songs, verses, run-sheets, settings and background images follow your login to any computer, and the app keeps working if the internet drops during a service. Changes sync again when it returns.',
    tip: 'Look for the sync status in the top bar. It shows when everything is saved.',
  },
  {
    title: 'Sermon Notes',
    icon: icon(Wand2),
    description: 'After the sermon, press Summarize in AI Tools for a theme, key points, scriptures, discussion questions and a weekly takeaway. Copy it, download it, or print a handout you can save as a PDF.',
    tip: 'Add a title and speaker name before printing the handout.',
  },
  {
    title: 'Saved Verses',
    icon: icon(Book),
    description: 'Save the verses you use every week for one-click access, and see the passages you opened recently in the Recent tab.',
    tip: 'Press ? at any time to see every keyboard shortcut.',
  },
];

/**
 * A step-by-step tour of the app. `onClose` is called when it is finished or closed.
 */
const AppTour = ({ onClose }) => {
  const [step, setStep] = useState(0);
  const current = TOUR_STEPS[step];

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm" role="dialog" aria-label="Tour of SleekHear">
      <div className="relative w-full max-w-lg max-h-[calc(100dvh-2rem)] overflow-y-auto bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl p-6 sm:p-8">
        <button
          onClick={onClose}
          aria-label="Close tour"
          className="absolute top-4 right-4 text-slate-500 hover:text-slate-300 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex gap-1.5 mb-6 pr-8">
          {TOUR_STEPS.map((_, i) => (
            <button
              key={i}
              onClick={() => setStep(i)}
              aria-label={`Go to step ${i + 1}`}
              className={`h-1.5 rounded-full flex-1 transition-all ${i === step ? 'bg-amber-400' : 'bg-slate-700 hover:bg-slate-600'}`}
            />
          ))}
        </div>

        <div className="flex items-start gap-4 mb-6">
          <div className="p-3 bg-slate-800 rounded-xl shrink-0">{current.icon}</div>
          <div>
            <p className="text-slate-500 text-xs font-semibold uppercase tracking-widest mb-1">
              Step {step + 1} of {TOUR_STEPS.length}
            </p>
            <h3 className="text-2xl font-bold text-slate-50 mb-3">{current.title}</h3>
            <p className="text-slate-300 leading-relaxed">{current.description}</p>
          </div>
        </div>

        <div className="bg-slate-800 border border-amber-500/20 rounded-lg px-4 py-3 mb-6">
          <p className="text-amber-400 text-sm"><span className="font-semibold">Tip:</span> {current.tip}</p>
        </div>

        <div className="flex items-center justify-between">
          <button
            onClick={() => setStep(s => s - 1)}
            disabled={step === 0}
            className="flex items-center gap-1 px-4 py-2 text-slate-400 hover:text-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="w-4 h-4" /> Previous
          </button>

          {step < TOUR_STEPS.length - 1 ? (
            <button
              onClick={() => setStep(s => s + 1)}
              className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-brand-600 to-brand-500 hover:from-brand-500 hover:to-blue-400 text-white font-semibold rounded-lg transition-all"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={onClose}
              className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-amber-500 to-amber-400 hover:from-amber-400 hover:to-amber-300 text-slate-900 font-semibold rounded-lg transition-all"
            >
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AppTour;
